import { getS, setS } from "./state";
import { getV } from "./binding";

/* =============================================
   ENTERPRISE RULE ENGINE
   - Runs all rules, collects filter/sort/paginate actions
   - Applies them in correct order: filter → sort → paginate
   ============================================= */
export const runRules = (rules, sectionId) => {
  if (!rules?.length) return;

  const s = getS();
  const baseList = s.list || [];

  let filteredList = [...baseList];
  let sortConfig = null;
  let pagination = { page: 1, pageSize: 12 };
  let dynamicOptions = { ...(s.dynamicOptions || {}) };
  let formUpdates = {};

  rules.forEach((rule) => {
    if (!matchCondition(rule.when)) return;

    rule.then.forEach((action) => {
      const type = String(action.action).trim().toLowerCase();

      switch (type) {
        case "filterlist":
          filteredList = applyFilter(filteredList, action);
          break;
        case "sort":
          sortConfig = action;
          break;
        case "paginate":
          pagination = { page: action.page || 1, pageSize: action.pageSize || 12 };
          break;
        case "setoptions":
          dynamicOptions[action.target] = action.value;
          break;
        case "set":
          formUpdates = setPath(formUpdates, action.target, action.value);
          break;
        case "reset":
          formUpdates = setPath(formUpdates, action.target, "");
          break;
        default:
          break;
      }
    });
  });

  /* ===== SORT ===== */
  if (sortConfig) {
    const { field, order = "asc" } = sortConfig;
    filteredList.sort((a, b) => {
      const aVal = a[field], bVal = b[field];
      if (!isNaN(aVal) && !isNaN(bVal)) return order === "asc" ? aVal - bVal : bVal - aVal;
      return order === "asc" ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
    });
  }

  /* ===== PAGINATE ===== */
  const total = filteredList.length;
  const start = (pagination.page - 1) * pagination.pageSize;
  const paginatedList = filteredList.slice(start, start + pagination.pageSize);

  /* ===== COMMIT ===== */
  setS({
    form: { ...(s.form || {}), ...(formUpdates.form || {}) },
    dynamicOptions: { ...(s.dynamicOptions || {}), ...dynamicOptions },
    screens: {
      ...(s.screens || {}),
      [sectionId]: {
        ...(s.screens?.[sectionId] || {}),
        filteredList: paginatedList,
        unfilteredList: filteredList,
        totalRecords: total,
        page: pagination.page,
        pageSize: pagination.pageSize,
      },
    },
  });
};

/* ===== CONDITION MATCHER ===== */
const matchCondition = (when) => {
  if (!when) return true;
  const fieldValue = getV(when.field);
  let match = true;
  if (when.equals !== undefined) match = match && String(fieldValue) === String(when.equals);
  if (when.notEquals !== undefined) match = match && fieldValue !== when.notEquals;
  if (when.notEmpty !== undefined) match = match && !!fieldValue && fieldValue !== "";
  if (when.empty !== undefined) match = match && (!fieldValue || fieldValue === "");
  return match;
};

/* ===== FILTER APPLICATOR ===== */
const applyFilter = (list, action) => {
  const rawVal = action.valueFrom ? getV(action.valueFrom) : action.value;

  return list.filter((item) => {
    const raw = item[action.field];
    const num = Number(raw);

    if (action.operator === "between") return num >= Number(action.min) && num <= Number(action.max);
    if (action.operator === "greaterThan") return num > Number(action.value);
    if (action.operator === "lessThan") return num < Number(action.value);
    if (action.operator === "equals") return String(raw).toLowerCase() === String(rawVal).toLowerCase();
    if (action.operator === "includes") return String(raw).toLowerCase().includes(String(rawVal).toLowerCase());
    if (action.operator === "contains") return String(raw).toLowerCase().includes(String(rawVal).toLowerCase());

    // default: string contains
    if (!rawVal) return true;
    return String(raw).toLowerCase().includes(String(rawVal).toLowerCase());
  });
};

/* ===== DEEP PATH SETTER ===== */
const setPath = (obj, path, value) => {
  const keys = path.split(".");
  const last = keys.pop();
  let cur = obj;
  keys.forEach((k) => { cur[k] = cur[k] || {}; cur = cur[k]; });
  cur[last] = value;
  return obj;
};
